import { PostMessageModel } from "../models/common/post-message.model";
import {
    graylogWebview,
    graylogIframe,
    GraylogAction
} from "./graylog-helper";

/**
 * Sanitize data before logging to remove sensitive information
 * @param data - The data to sanitize
 * @returns Sanitized data safe for logging
 */
const sanitizeDataForLogging = (data: any): any => {
    if (!data) return null;

    // List of sensitive keys to redact
    const sensitiveKeys = [
        'accessToken', 'access_token', 'refreshToken', 'refresh_token',
        'token', 'password', 'secret', 'apiKey', 'api_key', 'authorization',
        'oauth2', 'authState', 'auth'
    ];

    try {
        const sanitized = JSON.parse(JSON.stringify(data));

        const redactSensitive = (obj: any): any => {
            if (!obj || typeof obj !== 'object') return obj;

            for (const key of Object.keys(obj)) {
                if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk.toLowerCase()))) {
                    obj[key] = '[REDACTED]';
                } else if (typeof obj[key] === 'object') {
                    redactSensitive(obj[key]);
                }
            }
            return obj;
        };

        return redactSensitive(sanitized);
    } catch {
        return '[Unable to sanitize]';
    }
};

/**
 * Log WINAPP message received to Graylog
 * @param eventName - The event/message name from WINAPP
 * @param data - The data payload from WINAPP
 * @param serviceName - The service/component name that is calling this function
 */
export const logWinAppMessage = (eventName: string, data?: any, serviceName?: string) => {
    graylogWebview(
        serviceName || 'Unknown',
        GraylogAction.MESSAGE_RECEIVED,
        {
            eventName: eventName,
            data: sanitizeDataForLogging(data),
            timestamp: new Date().toISOString()
        }
    );
};

/**
 * Log message sent to WINAPP to Graylog
 * @param method - The method/event name being sent to WINAPP
 * @param param - The data payload being sent
 * @param serviceName - The service/component name that is calling this function
 */
const logMessageToWinApp = (method: string, param?: any, serviceName?: string) => {
    graylogWebview(
        serviceName || 'Unknown',
        GraylogAction.MESSAGE_SENT,
        {
            method: method,
            data: sanitizeDataForLogging(param),
            timestamp: new Date().toISOString()
        }
    );
};

/**
 * Log message sent to iframe (3rd party service) to Graylog
 * @param iframeName - The name/identifier of the iframe
 * @param message - The message being sent to iframe
 * @param serviceName - The service/component name that is calling this function
 */
export const logIframeMessageSent = (iframeName: string, message?: any, serviceName?: string) => {
    graylogIframe(
        serviceName || 'Unknown',
        GraylogAction.MESSAGE_SENT,
        iframeName,
        {
            data: sanitizeDataForLogging(message),
            timestamp: new Date().toISOString()
        }
    );
};

/**
 * Log message received from iframe (3rd party service) to Graylog
 * @param iframeName - The name/identifier of the iframe
 * @param message - The message received from iframe
 * @param serviceName - The service/component name that is calling this function
 */
export const logIframeMessageReceived = (iframeName: string, message?: any, serviceName?: string) => {
    graylogIframe(
        serviceName || 'Unknown',
        GraylogAction.MESSAGE_RECEIVED,
        iframeName,
        {
            data: sanitizeDataForLogging(message),
            timestamp: new Date().toISOString()
        }
    );
};

export const postMessageForWebview = (method: string, param?: any, serviceName?: string) => {
    try {
        let message = new PostMessageModel(method, param);
        let vm = window as any;
        console.log("win", vm.chrome);
        console.log("mobile-ios", vm.webkit);
        console.log("mobile-android", vm.jsHandler);
        console.log("message", message);

        // Log message sent to WINAPP
        logMessageToWinApp(method, param, serviceName);

        if (vm.chrome && vm.chrome.webview) {
            // win app
            console.log("win-webview", vm.chrome.webview);
            vm.chrome.webview.postMessage(message);
        } else if (vm.webkit && vm.webkit.messageHandlers) {
            // ios
            console.log(
                "mobile-ios-messageHandlers",
                vm.webkit.messageHandlers
            );
            vm.webkit.messageHandlers.jsHandler.postMessage(message);
        } else if (vm.jsHandler) {
            // android
            console.log("mobile-android-message", message.method);
            vm.jsHandler.postMessage(message.method.toString());
        }
    } catch (error) {
        console.log("postMessageForWebview", error);
    }
};
